import { Button, Flex, Space, Spin, Table, Tag, Tooltip, type TableProps } from 'antd'
import dayjs from 'dayjs'
import relativeTime from 'dayjs/plugin/relativeTime'
import { Fragment } from 'react'

import { TrashIcon } from '@phosphor-icons/react'
import { InviteeService } from '@services/InviteeService'
import { SchoolService } from '@services/SchoolService'
import { useConfirmation } from '@store/confirmation'
import { useNotifications } from '@store/notifications'

import type { Invitee } from '../../../../../types'

dayjs.extend(relativeTime)

if (!import.meta.env.VITE_BASE_APP_URL) {
    throw new Error('VITE_BASE_APP_URL env variable not setup')
}

const APP_BASE_URL = import.meta.env.VITE_BASE_APP_URL

export const InviteeTable = ({
    schoolId,
    invitees,
    refetch
}: {
    schoolId: string
    invitees: Invitee[]
    refetch: () => Promise<void>
}) => {
    const confirm = useConfirmation()
    const { showNotification } = useNotifications()

    const handleRemove = async (inviteeId: string) => {
        if (
            await confirm({
                title: 'Are you sure you want to remove this invite?',
                confirmText: 'Remove'
            })
        ) {
            try {
                await SchoolService.removeInvite(schoolId, inviteeId)
                showNotification({ title: 'Invite removed' })
                await refetch()
            } catch (err) {
                showNotification({ title: 'Error removing invite', type: 'error' })
            }
        }
    }

    const onCopyInvite = async (inviteeId: string, verificationCode: string) => {
        const link = `${APP_BASE_URL}/accept-invite?t=${encodeURIComponent(verificationCode)}`

        navigator.clipboard.writeText(link)

        showNotification({ title: 'Link copied to clipboard' })

        try {
            // Renew expiry
            await InviteeService.copyInviteLink(inviteeId)
        } catch (err) {
            console.error(err)
            // do nothing
            showNotification({ title: 'Error copying invite', type: 'error' })
        }
    }

    const columns: TableProps<Invitee>['columns'] = [
        {
            title: 'Email',
            dataIndex: 'email'
        },
        {
            title: 'Subscription',
            key: 'subscriptions',
            render: (_, { claims }) => {
                return (
                    <>
                        {claims?.map(claim => {
                            if (claim.schoolSubscription) {
                                if (
                                    claim.schoolSubscription.subscription.status === 'COMPLETED' ||
                                    claim.schoolSubscription.subscription.status === 'CANCELLED'
                                ) {
                                    return <Tag key={claim.schoolSubscriptionId}>Subscription has expired</Tag>
                                }
                                return (
                                    <Space key={claim.schoolSubscriptionId}>
                                        <Tag key={claim.schoolSubscription?.subscription.id}>
                                            {claim.schoolSubscription?.subscription?.name ||
                                                claim.schoolSubscription?.subscription.subscriptionProducts
                                                    .map(sp => sp.product.key)
                                                    .join(', ')}
                                        </Tag>
                                    </Space>
                                )
                            }
                            return null
                        })}
                    </>
                )
            }
        },
        {
            title: 'Role',
            key: 'role',
            render: (_, { claims }) => {
                return (
                    <>
                        {claims?.map(claim => {
                            if (claim.role) {
                                return <p key={claim.id}>{claim.role}</p>
                            }
                            return null
                        })}
                    </>
                )
            }
        },
        {
            title: 'Time Invited',
            key: 'invitedOn',
            render: (_, { createdAt }) => <>{dayjs(createdAt).fromNow()}</>
        },
        {
            title: 'Expires',
            key: 'expiry',
            render: (_, { expiry }) => (
                <>
                    {dayjs(expiry).format('MMM D, YYYY')} ({dayjs(expiry).fromNow()})
                </>
            )
        },
        {
            title: 'Action',
            key: 'action',
            render: (_, { id: inviteeId, claims, verificationCode }) => {
                const invalidSubscriptions =
                    claims?.filter(claim => {
                        if (claim.schoolSubscription) {
                            if (
                                claim.schoolSubscription.subscription.status === 'COMPLETED' ||
                                claim.schoolSubscription.subscription.status === 'CANCELLED'
                            ) {
                                return true
                            }
                        }
                        return false
                    }) || []
                const isInvalid = invalidSubscriptions.length > 0
                const TT = isInvalid ? Tooltip : Fragment

                return (
                    <Flex gap="middle" align="center">
                        <TT title="Invite is now invalid. Please delete and send a new invite">
                            <Button
                                disabled={isInvalid}
                                type="link"
                                onClick={async () => {
                                    if (
                                        await confirm({
                                            title: 'Do you want to resend the invite?'
                                        })
                                    ) {
                                        try {
                                            await InviteeService.resendInvite(inviteeId)
                                            showNotification({ title: 'Invite sent!' })
                                        } catch (err) {
                                            showNotification({ title: 'Failed to resend invite', type: 'error' })
                                        }
                                    }
                                }}
                            >
                                Resend Invite
                            </Button>
                            <Button
                                type="link"
                                onClick={async () => {
                                    await onCopyInvite(inviteeId, verificationCode)
                                }}
                            >
                                Copy Invite Link
                            </Button>
                        </TT>

                        <TrashIcon size={20} color="red" onClick={() => handleRemove(inviteeId)} cursor="pointer" />
                    </Flex>
                )
            }
        }
    ]

    if (!invitees) return <Spin />

    return (
        <Table
            columns={columns}
            dataSource={invitees || []}
            rowKey="id"
            pagination={{ pageSize: 50 }}
            styles={{
                content: {
                    overflow: 'scroll'
                }
            }}
        />
    )
}
